import { cn } from "@omni/utils/styles";
import { ReactNode } from "react";
import {
    Controller,
    ControllerRenderProps,
    FieldValues,
    Path,
    useFormContext
} from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { FormText } from "@/components/Form/Base/formText.component";
import { MicroCopy } from "@/components/Form/Base/microCopy.component";

import { FormFeedback } from "../../FormElements/formFeedback.component";
import { calculateFieldState } from "../../Utils/form.helpers";
import { isFormRequired } from "../formField.types";

export type BaseFieldProps<T extends FieldValues, TPath extends Path<T>> = {
    name: TPath;
    label: string;
    microCopy?: string | (() => ReactNode);
    required: isFormRequired<T, TPath>;
    hideLabel?: boolean;
    hideMicroCopy?: boolean;
    test?: string;
    optionalIndication?: boolean;
    requiredIndication?: boolean;
    className?: string;
    tooltip?: { title: string; body: string };
};

export type BaseFieldChildProps<
    T extends FieldValues,
    TPath extends Path<T>
> = {
    field: ControllerRenderProps<T, TPath>;
    fieldState: {
        isInvalid: boolean;
        isValid: boolean;
    };
};

export type BaseFieldPropsWithChildren<
    T extends FieldValues,
    TPath extends Path<T>
> = BaseFieldProps<T, TPath> & {
    children: (props: BaseFieldChildProps<T, TPath>) => ReactNode;
};

export type BaseFieldPropsWithOptionalChildren<
    T extends FieldValues,
    TPath extends Path<T>
> = BaseFieldProps<T, TPath> & {
    children?: (props: BaseFieldChildProps<T, TPath>) => ReactNode;
};

const BaseField = <T extends FieldValues, TPath extends Path<T>>({
    children,
    name,
    label,
    microCopy,
    required,
    hideLabel = false,
    hideMicroCopy = false,
    optionalIndication,
    requiredIndication,
    className,
    tooltip
}: BaseFieldPropsWithChildren<T, TPath>) => {
    const { control } = useFormContext<T>();

    return (
        <FormGroup className={cn(className)}>
            <FieldLabel
                name={name}
                label={label}
                required={required}
                hideLabel={hideLabel}
                requiredIndication={requiredIndication}
                optionalIndication={optionalIndication}
                tooltip={tooltip}
            />
            {!hideMicroCopy &&
                microCopy &&
                (microCopy instanceof Function ? (
                    microCopy()
                ) : (
                    <FormText
                        inline={true}
                        color="default"
                    >
                        {microCopy}
                    </FormText>
                ))}
            {!hideMicroCopy && !microCopy && <MicroCopy fieldId={name} />}
            <Controller
                control={control}
                name={name}
                render={({ field, fieldState, formState }) => {
                    const newFieldState = calculateFieldState<T>(
                        fieldState,
                        formState
                    );
                    return (
                        <>
                            {children({
                                field: field,
                                fieldState: newFieldState
                            })}
                            <FormFeedback
                                name={name}
                                label={label}
                            />
                        </>
                    );
                }}
            />
        </FormGroup>
    );
};

export { BaseField };
